import React, { createContext, FC, useEffect, useMemo, useState, useContext } from "react";
import { useMutation } from "@tanstack/react-query";
import { cloneDeep, isEqual, Many, orderBy, sum } from "lodash";
import { toast } from "react-toastify";
import {
  MedicareNetwork,
  MedicareParentOrganization,
  MedicareParentOrganizationSortingTypeEnum,
  MedicareStarRatingTypeEnum,
} from "@app/overviews/network-benchmarking-dashboard/medicare/filters";
import { BenchmarkingPlanTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingPlanTypeEnum";
import { BenchmarkingDashboardSnpPlanFilterEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details/shared/BenchmarkingDashboardSnpPlanFilterEnum";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import BenchmarkingService from "@app/overviews/network-benchmarking-dashboard/benchmarkingService";
import { BenchmarkingLineOfBusinessEnum, BenchmarkingCounty, CompetitiveInsightsTabTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingNetworkType";
import { BenchmarkingParentOrganizationType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingParentOrganizationType";
import { CompetitiveInsightsSelectedSortType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/competitiveInsightsSelectedSortType";
import { useMedicarePrimaryFilters } from "@app/overviews/network-benchmarking-dashboard/medicare/filters/hooks/useMedicarePrimaryFilters";
import { MedicarePrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/medicare/filters/shared/MedicarePrimaryFiltersPayload";
import { MembershipCountTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/MembershipCountTypeEnum";
import { UserContext } from "@app/user/UserContext";

export interface MedicareFiltersContextValues {
  applyingServiceArea: MedicareNetwork[];
  handleSetSelectedSortType: (sortType: CompetitiveInsightsSelectedSortType) => void;
  mappedPlanTypes: DropdownItem<BenchmarkingPlanTypeEnum>[];
  mappedStarRatings: DropdownItem<MedicareStarRatingTypeEnum>[];
  networkFiltersLoading: boolean;
  onApplyPrimaryFiltersClick: () => void;
  onApplyServiceAreaClick: (countyIds: number[], contracts: BenchmarkingNetworkType[]) => void;
  onClearNetworkFiltersClick: () => void;
  onClearPrimaryFiltersClick: () => void;
  parentOrganizations: MedicareParentOrganization[]
  primaryFiltersLoading: boolean;
  selectedMembershipCount: MembershipCountTypeEnum;
  selectedParentOrganizations: MedicareParentOrganization[];
  selectedSortType: MedicareParentOrganizationSortingTypeEnum;
  selectedPlanTypes: DropdownItem<BenchmarkingPlanTypeEnum>[];
  selectedStarRatings: DropdownItem<MedicareStarRatingTypeEnum>[];
  selectedSnpFilter: BenchmarkingDashboardSnpPlanFilterEnum;
  setSelectedMembershipCount: (selectedMembershipCount: MembershipCountTypeEnum) => void;
  setSelectedParentOrganizations: (selectedParentOrganizations: MedicareParentOrganization[]) => void;
  setSelectedSortType: (selectedSortType: MedicareParentOrganizationSortingTypeEnum) => void;
  setSelectedPlanTypes: (selectedPlanTypes: DropdownItem<BenchmarkingPlanTypeEnum>[]) => void;
  setSelectedStarRatings: (selectedStarRatings: DropdownItem<MedicareStarRatingTypeEnum>[]) => void;
  setSelectedSnpFilter: (selectedSnpFilter: BenchmarkingDashboardSnpPlanFilterEnum) => void;
  sortNetworks: (filteredParents: BenchmarkingParentOrganizationType[]) => BenchmarkingParentOrganizationType[];
}

const initialValues: MedicareFiltersContextValues = {
  applyingServiceArea: [],
  handleSetSelectedSortType: () => null,
  mappedPlanTypes: [],
  mappedStarRatings: [],
  networkFiltersLoading: false,
  onApplyPrimaryFiltersClick: () => null,
  onApplyServiceAreaClick: () => null,
  onClearNetworkFiltersClick: () => null,
  onClearPrimaryFiltersClick: () => null,
  parentOrganizations: [],
  primaryFiltersLoading: false,
  selectedMembershipCount: MembershipCountTypeEnum.All,
  selectedParentOrganizations: [],
  selectedPlanTypes: [],
  selectedSnpFilter: BenchmarkingDashboardSnpPlanFilterEnum.All,
  selectedSortType: null,
  selectedStarRatings: [],
  setSelectedMembershipCount: () => null,
  setSelectedParentOrganizations: () => null,
  setSelectedPlanTypes: () => null,
  setSelectedSnpFilter: () => null,
  setSelectedSortType: () => null,
  setSelectedStarRatings: () => null,
  sortNetworks: () => null,
}

export const MedicareFiltersContext = createContext(initialValues);

interface ApplyPrimaryFiltersVariables {
  applyingServiceArea: MedicareNetwork[];
  primaryFilters: MedicarePrimaryFiltersPayload;
}

const MedicareFiltersContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  const [applyingServiceArea, setApplyingServiceArea] = useState<MedicareNetwork[]>(initialValues.applyingServiceArea);
  const [mappedPlanTypes, setMappedPlanTypes] = useState<DropdownItem<BenchmarkingPlanTypeEnum>[]>(initialValues.mappedPlanTypes);
  const [mappedStarRatings, setMappedStarRatings] = useState<DropdownItem<MedicareStarRatingTypeEnum>[]>(initialValues.mappedStarRatings);
  const [parentOrganizations, setParentOrganizations] = useState<MedicareParentOrganization[]>(initialValues.parentOrganizations);
  const [selectedMembershipCount, setSelectedMembershipCount] = useState<MembershipCountTypeEnum>(initialValues.selectedMembershipCount);
  const [selectedParentOrganizations, setSelectedParentOrganizations] = useState<MedicareParentOrganization[]>([]);
  const [selectedPlanTypes, setSelectedPlanTypes] = useState<DropdownItem<BenchmarkingPlanTypeEnum>[]>(initialValues.selectedPlanTypes);
  const [selectedSortType, setSelectedSortType] = useState<MedicareParentOrganizationSortingTypeEnum>(null);
  const [selectedStarRatings, setSelectedStarRatings] = useState<DropdownItem<MedicareStarRatingTypeEnum>[]>(initialValues.selectedStarRatings);
  const [selectedSnpFilter, setSelectedSnpFilter] = useState<BenchmarkingDashboardSnpPlanFilterEnum>(initialValues.selectedSnpFilter);

 const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const { clientId } = useContext(UserContext);

  const benchmarkingService = useMemo(() => new BenchmarkingService(), []);
  const { isPending: networkFiltersLoading, mutate: applyPrimaryFiltersMutation } = useMutation({
    mutationFn: ({ primaryFilters }: ApplyPrimaryFiltersVariables) => benchmarkingService.getBenchmarkingNetworkData(primaryFilters),
    onSuccess: (data, variables) => {
      if (!data) return;

      const { primaryFilters } = variables;

      const filterComparisonNetworks = (parentOrganizations: MedicareParentOrganization[]): MedicareParentOrganization[] => {
        // membership count and star rating filters will only be applied to the competiter networks, not your qes networks
        const filteredParentOrganizations: MedicareParentOrganization[] = [];

        for (const parentOrganization of parentOrganizations) {
          // only apply a membership count filter if all isn't the selected membership count
          if (primaryFilters.membershipCount !== MembershipCountTypeEnum.All) {
            parentOrganization.hContracts = parentOrganization.hContracts.filter(x => x.membership >= parseInt(primaryFilters.membershipCount.replace(/,/g, "")));
          }

          if (primaryFilters.snpFilter !== BenchmarkingDashboardSnpPlanFilterEnum.All) {
            const snpFilter = primaryFilters.snpFilter === BenchmarkingDashboardSnpPlanFilterEnum.Yes ? true : false;
            parentOrganization.hContracts = parentOrganization.hContracts.filter(x => x.isSnpPlan === snpFilter);
          }

          // only apply a star rating filter if all of the star rating options aren't selected
          if (primaryFilters.starRatings.length !== mappedStarRatings.length) {
            parentOrganization.hContracts = parentOrganization.hContracts.filter(
              x => primaryFilters.starRatings.includes((!!x.rating ? x.rating.toString() : "N/A") as MedicareStarRatingTypeEnum)
            );
          }

          // only apply a plan type filter if all of the plan type options aren't selected
          if (primaryFilters.planTypes.length !== mappedPlanTypes.length) {
            parentOrganization.hContracts = parentOrganization.hContracts.filter(
              x => primaryFilters.planTypes.includes((!!getPlanTypeShortenedName(x.planType) ? getPlanTypeShortenedName(x.planType.toString()) : "N/A") as BenchmarkingPlanTypeEnum)
            );
          }

          // do not display any parent organization that no longer has h contracts visible due to the newly applied filters
          if (!!parentOrganization.hContracts.length) {
            filteredParentOrganizations.push(parentOrganization);
          }
        }

        return filteredParentOrganizations;
      }

      const yourNetworks: MedicareParentOrganization = {
        hContracts: data.yourNetworks,
        parentOrganizationName: "Your QES Networks",
      };

      for (const parentOrg of data.comparisonNetworks) {
        for (const contract of parentOrg.hContracts) {
          contract.parentOrganization = parentOrg.parentOrganizationName;
        }
      }

      let newParentOrganizations = [yourNetworks];
      newParentOrganizations = newParentOrganizations.concat(filterComparisonNetworks(data.comparisonNetworks));

      for (const parentOrg of newParentOrganizations) {
        for (const contract of parentOrg.hContracts) {
          contract.parentOrganization = parentOrg.parentOrganizationName;
        }
      }

      setParentOrganizations(newParentOrganizations);

      const newSelectedParentOrganizations: MedicareParentOrganization[] = [cloneDeep(yourNetworks)];
      const newSelectedNetworks: MedicareNetwork[] = [];

      const handlePreviousSelections = (serviceArea: BenchmarkingNetworkType): void => {
        const parentOrganization: MedicareParentOrganization = newParentOrganizations.find(x => x.parentOrganizationName === serviceArea.parentOrganization);

        if (!!parentOrganization) {
          const newContract: MedicareNetwork = parentOrganization.hContracts.find(x => x.contractId === serviceArea.contractId);

          // the previously-selected contract must be re-acquired in order to obtain the latest map data
          if (!!newContract) {
            newSelectedNetworks.push(newContract);
          }

          // if the parent organization of the previously-selected contract is not your qes networks (which is already selected by default),
          // and if it is not already selected, select it
          if (parentOrganization.parentOrganizationName !== yourNetworks.parentOrganizationName && !newSelectedParentOrganizations.includes(parentOrganization)) {
            newSelectedParentOrganizations.push(parentOrganization);
          }
        }
      }

      if (!!variables.applyingServiceArea.length) {
        // when applying service area
        toast("Service Area Updated", {
          containerId: "z-index-9999",
          type: "success",
        });

        for (const serviceArea of variables.applyingServiceArea) {
          if (!serviceArea.disabled) {
            handlePreviousSelections(serviceArea);
          }
        }

        if (benchmarkingFiltersContext.selectedTab !== CompetitiveInsightsTabTypeEnum.NetworkSelection) {
          benchmarkingFiltersContext.setSelectedTab(CompetitiveInsightsTabTypeEnum.NetworkSelection)
        }

        setApplyingServiceArea([]);
      } else {
        // when retaining networks from a previous search
        for (const previousSelectedNetwork of benchmarkingFiltersContext.previousSelectedNetworks) {
          if (newSelectedNetworks.length < 5) {
            handlePreviousSelections(previousSelectedNetwork);
          }
        }
      }

      benchmarkingFiltersContext.setSelectedNetworks(newSelectedNetworks);
      setSelectedParentOrganizations(newSelectedParentOrganizations);
    },
  });

  const starRatings: DropdownItem<MedicareStarRatingTypeEnum>[] = Object.values(MedicareStarRatingTypeEnum).map(x => {
    return { key: x, name: x }
  });

  const planTypes: DropdownItem<BenchmarkingPlanTypeEnum>[] = Object.values(BenchmarkingPlanTypeEnum).map(x => {
    return { key: x, name: x }
  });

  const isMedicare = benchmarkingFiltersContext.selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Medicare;

  const { isLoading: primaryFiltersLoading, primaryFilterData } = useMedicarePrimaryFilters(clientId, isMedicare);

  useEffect(() => {
    if (!isMedicare) return;

    benchmarkingFiltersContext.setDisplayedPrimaryFilters(null);
    benchmarkingFiltersContext.setSelectedState(null);
    benchmarkingFiltersContext.setCounties([]);
    benchmarkingFiltersContext.setRawCounties([]);
    benchmarkingFiltersContext.setSelectedCounties([]);
    benchmarkingFiltersContext.setCountyIdFilter(null);
    setSelectedMembershipCount(MembershipCountTypeEnum.All);
    setMappedStarRatings(cloneDeep(starRatings));
    setSelectedStarRatings(cloneDeep(starRatings));
    setMappedPlanTypes(cloneDeep(planTypes));
    setSelectedPlanTypes(cloneDeep(planTypes));
    setSelectedSnpFilter(BenchmarkingDashboardSnpPlanFilterEnum.All);
  }, [isMedicare]);

  useEffect(() => {
    if (!primaryFilterData || !isMedicare) return;

    benchmarkingFiltersContext.processSpecialties(primaryFilterData.specialties);
    benchmarkingFiltersContext.setStates(primaryFilterData.states);
    benchmarkingFiltersContext.setAffiliationAuthorizedStates(primaryFilterData.affiliationAuthorizedStates);
    benchmarkingFiltersContext.setMarketProjectId(primaryFilterData.marketProjectId);
  }, [primaryFilterData, isMedicare]);

  useEffect(() => {
    if (!!selectedSortType) {
      setSelectedSortType(null);
    }
  }, [networkFiltersLoading]);

  useEffect(() => {
    if (!!parentOrganizations?.length && !selectedSortType) {
      setSelectedSortType(MedicareParentOrganizationSortingTypeEnum.MembershipDesc);
    }
  }, [parentOrganizations]);

  useEffect(() => {
    if (!!applyingServiceArea.length) {
      onApplyPrimaryFiltersClick();
    } else {
      const countiesEqual = isEqual(
        benchmarkingFiltersContext.displayedPrimaryFilters?.countyIds?.map(x => x.toString()).sort((a, b) => a.localeCompare(b)),
        benchmarkingFiltersContext.selectedCounties.sort((a, b) => a.localeCompare(b))
      );

      const starRatingsEqual = isEqual(
        benchmarkingFiltersContext.displayedPrimaryFilters?.starRatings?.sort(),
        selectedStarRatings.map(x => x.key).sort()
      );

      const planTypesEqual = isEqual(
        benchmarkingFiltersContext.displayedPrimaryFilters?.planTypes?.sort(),
        selectedPlanTypes.map(x => x.key).sort()
      );

      const snpFilterEqual = (benchmarkingFiltersContext.displayedPrimaryFilters as MedicarePrimaryFiltersPayload)?.snpFilter === selectedSnpFilter;

      const membershipCountEqual = (benchmarkingFiltersContext.displayedPrimaryFilters as MedicarePrimaryFiltersPayload)?.membershipCount === selectedMembershipCount;

      if ((!countiesEqual || !starRatingsEqual || !planTypesEqual || !snpFilterEqual || !membershipCountEqual) && !!benchmarkingFiltersContext.selectedNetworks.length) {
        benchmarkingFiltersContext.setPreviousSelectedNetworks(cloneDeep(benchmarkingFiltersContext.selectedNetworks));
      }

      if (!!benchmarkingFiltersContext.displayedPrimaryFilters || !!benchmarkingFiltersContext.displayedNetworkFilters?.length) {
        benchmarkingFiltersContext.setDisplayedPrimaryFilters(null);
        onClearNetworkFiltersClick();
      }
    }
  }, [
    benchmarkingFiltersContext.selectedState,
    benchmarkingFiltersContext.selectedCounties,
    selectedMembershipCount,
    selectedStarRatings,
    selectedPlanTypes,
    selectedSnpFilter
  ]);

  useEffect(() => {
    if (!!benchmarkingFiltersContext.displayedNetworkFilters?.length) {
      // on network change, clear all displayed network filters
      benchmarkingFiltersContext.setDisplayedNetworkFilters([]);
    }
  }, [benchmarkingFiltersContext.selectedNetworks, selectedParentOrganizations]);

  const handleSetSelectedSortType = (sortType: CompetitiveInsightsSelectedSortType) => {
    setSelectedSortType(sortType as MedicareParentOrganizationSortingTypeEnum);
  }

  const onApplyServiceAreaClick = (countyIds: number[], contracts: BenchmarkingNetworkType[]): void => {
    const newSelectedCounties: BenchmarkingCounty[] = benchmarkingFiltersContext.rawCounties.filter(x => countyIds.includes(x.countyId));
    benchmarkingFiltersContext.setSelectedCounties(newSelectedCounties.map(x => x.countyId.toString()));
    setApplyingServiceArea(contracts as MedicareNetwork[]);
  }

  const onApplyPrimaryFiltersClick = (): void => {
    const primaryFilters: MedicarePrimaryFiltersPayload = {
      countyIds: benchmarkingFiltersContext.selectedCounties.map(x => parseInt(x)),
      lineOfBusinessId: benchmarkingFiltersContext.selectedLineOfBusiness,
      membershipCount: selectedMembershipCount,
      planTypes: selectedPlanTypes.map(x => x.key),
      snpFilter: selectedSnpFilter,
      starRatings: selectedStarRatings.map(x => x.key),
    };

    benchmarkingFiltersContext.setDisplayedPrimaryFilters(primaryFilters);

    applyPrimaryFiltersMutation({ applyingServiceArea, primaryFilters });
  }

  const onClearPrimaryFiltersClick = (): void => {
    benchmarkingFiltersContext.setSelectedCounties([]);
    benchmarkingFiltersContext.setCounties([]);
    benchmarkingFiltersContext.setRawCounties([]);
    benchmarkingFiltersContext.setSelectedState(null);
    benchmarkingFiltersContext.setDisplayedPrimaryFilters(null);
    setSelectedMembershipCount(MembershipCountTypeEnum.All);
    setSelectedStarRatings(cloneDeep(starRatings));
    setSelectedPlanTypes(cloneDeep(planTypes));
    setSelectedSnpFilter(BenchmarkingDashboardSnpPlanFilterEnum.All);

    // if resetting the primary filters,
    // also reset the network filters that are based off of the primary filters
    onClearNetworkFiltersClick();
  }

  const onClearNetworkFiltersClick = (): void => {
    setSelectedParentOrganizations([]);
    benchmarkingFiltersContext.setSelectedNetworks([]);
    benchmarkingFiltersContext.setDisplayedNetworkFilters([]);
  }

  const getPlanTypeShortenedName = (planType: string): string => {
    if (!planType) {
      return "";
    } else if (planType.toUpperCase().includes("MEDICARE-MEDICAID")) {
      return "MMP";
    } else if (planType.toUpperCase().includes("1876 COST")) {
      return "1876";
    } else if (planType.toUpperCase().includes("HMO/HMOPOS")) {
      return "HMO";
    } else if (planType.toUpperCase().includes("LOCAL PPO") || planType.toUpperCase().includes("REGIONAL PPO")) {
      return "PPO";
    } else {
      return planType;
    }
  }

  const sortByMembership = (qesNetwork: MedicareParentOrganization, benchmarkingParentOrganizations: MedicareParentOrganization[], sortDirection: Many<boolean | "asc" | "desc">): MedicareParentOrganization[] => {
    benchmarkingParentOrganizations = orderBy(benchmarkingParentOrganizations, (p: MedicareParentOrganization) => sum(p.hContracts.map(x => x.membership)), sortDirection);
    benchmarkingParentOrganizations.forEach((p) => {
      const sortedHContracts = orderBy(p.hContracts, (h: MedicareNetwork) => h.membership, sortDirection);
      p.hContracts = sortedHContracts;
    });

    if (!!qesNetwork) {
      qesNetwork.hContracts = orderBy(qesNetwork.hContracts, (h: MedicareNetwork) => h.membership, sortDirection);
    }

    return benchmarkingParentOrganizations;
  }

  const sortByName = (qesNetwork: MedicareParentOrganization, benchmarkingParentOrganizations: MedicareParentOrganization[], sortDirection: Many<boolean | "asc" | "desc">): MedicareParentOrganization[] => {
    benchmarkingParentOrganizations = orderBy(benchmarkingParentOrganizations, (p) => p.parentOrganizationName, sortDirection);
    benchmarkingParentOrganizations.forEach((p) => {
      const sortedHContracts = orderBy(p.hContracts, (h) => h.contractId, sortDirection);
      p.hContracts = sortedHContracts;
    });

    if (!!qesNetwork) {
      qesNetwork.hContracts = orderBy(qesNetwork.hContracts, (h) => h.contractId, sortDirection);
    }

    return benchmarkingParentOrganizations;
  }

  const sortNetworks = (filteredParents: BenchmarkingParentOrganizationType[]): BenchmarkingParentOrganizationType[] => {
    if (!!selectedSortType) {
      // "your qes networks" pill should be excluded from sorting and should always maintain the first position
      const medicareParentOrganizations: MedicareParentOrganization[] = filteredParents as MedicareParentOrganization[];
      const yourQesNetworks = cloneDeep(medicareParentOrganizations.find(x => x.parentOrganizationName === "Your QES Networks"));
      let newSortedBenchmarkingParentOrganizations = cloneDeep(medicareParentOrganizations.filter(x => x.parentOrganizationName !== "Your QES Networks"));

      switch (selectedSortType) {
        case MedicareParentOrganizationSortingTypeEnum.AlphabeticalAsc:
          newSortedBenchmarkingParentOrganizations = sortByName(yourQesNetworks, newSortedBenchmarkingParentOrganizations, "asc");
          break;
        case MedicareParentOrganizationSortingTypeEnum.AlphabeticalDesc:
          newSortedBenchmarkingParentOrganizations = sortByName(yourQesNetworks, newSortedBenchmarkingParentOrganizations, "desc");
          break;
        case MedicareParentOrganizationSortingTypeEnum.MembershipAsc:
          newSortedBenchmarkingParentOrganizations = sortByMembership(yourQesNetworks, newSortedBenchmarkingParentOrganizations, "asc");
          break;
        case MedicareParentOrganizationSortingTypeEnum.MembershipDesc:
          newSortedBenchmarkingParentOrganizations = sortByMembership(yourQesNetworks, newSortedBenchmarkingParentOrganizations, "desc");
          break;
        default:
          break;
      }

      filteredParents = !!yourQesNetworks ? [yourQesNetworks] : [];
      filteredParents = filteredParents.concat(newSortedBenchmarkingParentOrganizations)

      //update the selectedParentOrganizations with the sorted items so the UI gets updated
      selectedParentOrganizations.forEach((p, index: number) => {
        const selectedParentIndex = filteredParents.findIndex(o => o.parentOrganizationName === p.parentOrganizationName);
        if (selectedParentIndex > -1) {
          selectedParentOrganizations.splice(index, 1, (filteredParents as MedicareParentOrganization[])[selectedParentIndex]);
        }
      });
    }

    return filteredParents;
  }

  return (
    <MedicareFiltersContext.Provider
      value={{
        applyingServiceArea,
        handleSetSelectedSortType,
        mappedPlanTypes,
        mappedStarRatings,
        networkFiltersLoading,
        onApplyPrimaryFiltersClick,
        onApplyServiceAreaClick,
        onClearNetworkFiltersClick,
        onClearPrimaryFiltersClick,
        parentOrganizations,
        primaryFiltersLoading,
        selectedMembershipCount,
        selectedParentOrganizations,
        selectedPlanTypes,
        selectedSnpFilter,
        selectedSortType,
        selectedStarRatings,
        setSelectedMembershipCount,
        setSelectedParentOrganizations,
        setSelectedPlanTypes,
        setSelectedSnpFilter,
        setSelectedSortType,
        setSelectedStarRatings,
        sortNetworks,
      }}>
      {props.children}
    </MedicareFiltersContext.Provider>
  )
}

export default MedicareFiltersContextProvider;